import React, { useCallback } from 'react';
import { CautionsTabLocationsView, ConsolidatedLocationView } from '@mark43/cad-api';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { VStack, Icon, Box } from '@cad-arc';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { CorrectedLocation } from '../types';
import { useViewLocationOnMap } from '../../location/hooks/use-view-location-on-map';
import { FR_EVENT_DETAILS_SUB_PATH, FR_BOLO_ROOT_PATH } from '../../router/constants';
import { LocationSelectValueDisplay } from '../../location-select/components/location-select-value-display';
import { GoogleMapsRouteButton } from '../../fr-event-common-components/google-maps-route-button';
import { EsriMapEntityTypeEnum } from '../../esri/types';
import { MaritimeFormatsButtonsSection } from '../../common/components/maritime-formats-buttons-section';
import { useMaritimeFormat } from '../../common/hooks/use-maritime-format';

interface FrEntityLocationSelectValueDisplayProps {
    showComplementaryContent?: boolean;
    cautions?: CautionsTabLocationsView;
    areCautionsLoading?: boolean;
    specificEntityLocation?: CorrectedLocation;
    cautionProps?: {
        highestPriorityLocationCaution: number | undefined;
        isNearbyLocationCautionPriority1: boolean;
    };
    entityId?: number;
    entityType: 'BOLO' | 'EVENT';
}

type EntityLocationSelectForm = {
    location: ConsolidatedLocationView | null;
};

export function FrEntityLocationSelectValueDisplay({
    showComplementaryContent = true,
    cautions,
    areCautionsLoading,
    specificEntityLocation,
    cautionProps,
    entityId,
    entityType,
}: FrEntityLocationSelectValueDisplayProps) {
    const isEvent = entityType === 'EVENT';
    const { mapRouteEnabled, maritimeCoordinatesEnabled } = useEnabledFeatures();
    const { highestPriorityLocationCaution, isNearbyLocationCautionPriority1 } = cautionProps ?? {};
    const form = useFormContext<EntityLocationSelectForm>();
    const viewLocationOnMap = useViewLocationOnMap();
    const entityLocation = useWatch({
        control: form.control,
        name: 'location',
    });
    const location = useLocation();
    const { formattedValue, maritimeFormat, setMaritimeFormat } = useMaritimeFormat({
        latitude: (specificEntityLocation || entityLocation)?.latitude,
        longitude: (specificEntityLocation || entityLocation)?.longitude,
    });

    const isFrEventDetailsPage = location.pathname.includes(
        isEvent ? FR_EVENT_DETAILS_SUB_PATH : FR_BOLO_ROOT_PATH
    );

    const handleViewOnMap = useCallback(() => {
        if (!entityId || !entityLocation?.areCoordinatesVerified || !isEvent) {
            return;
        }

        viewLocationOnMap({
            entityId,
            entityType: EsriMapEntityTypeEnum.EVENT,
            latitude: entityLocation?.latitude,
            longitude: entityLocation?.longitude,
            shouldMoveMapCenterToLoggedInUnit: false,
        });
    }, [entityId, entityLocation, viewLocationOnMap, isEvent]);

    return (
        <LocationSelectValueDisplay
            {...(entityLocation?.areCoordinatesVerified ? { onClick: handleViewOnMap } : {})}
            value={specificEntityLocation || entityLocation}
            complementaryContent={
                showComplementaryContent &&
                isEvent &&
                entityLocation?.areCoordinatesVerified && (
                    <>
                        <Icon
                            icon="Crosshairs"
                            color="brand.default"
                            style={{ marginLeft: 5, marginRight: 5 }}
                            data-testid="location-crosshairs-icon"
                            size="md"
                        />
                        {isFrEventDetailsPage && mapRouteEnabled && <GoogleMapsRouteButton />}
                    </>
                )
            }
            coordinatesBlockContent={
                maritimeCoordinatesEnabled && (
                    <VStack alignItems="start" m="8px auto">
                        <Box>{formattedValue}</Box>
                        <MaritimeFormatsButtonsSection
                            activeFormat={maritimeFormat}
                            setActiveFormat={setMaritimeFormat}
                        />
                    </VStack>
                )
            }
            cautionProps={{
                cautions: cautions?.wholeAddressCautionTabLocation,
                areCautionsLoading,
                isNearbyLocationCautionPriority1,
                highestPriorityLocationCaution,
            }}
            size="md"
            dataTestId="fr-entity-location-select-value-display"
        />
    );
}
